import type { OptionsDocument, PresentationOption, QuoteDocument, QuoteTerms, RatesDocument } from 'paintscout'
import { replaceLineItems } from '../replace-line-items'
import { replaceRates } from '../replace-rates'

/**
 * Replase the line items and substrates on a quote with their updated versions from options/rates if they exist
 */
export function replaceQuoteItems({
  quote,
  options,
  rates
}: {
  quote: Partial<QuoteDocument>
  options: OptionsDocument
  rates?: RatesDocument
}): Partial<QuoteDocument> {
  // need to get terms value from the quote type so stays synced
  const { termsValue, presentationValue } = getOptionsValues({ quote, options })

  const quoteTypeHourlyRate = options.options.intExtOptions?.values[quote?.int_ext?.value]?.hourlyRate ?? 0
  const liveHourlyRate = Number(
    quoteTypeHourlyRate > 0 ? quoteTypeHourlyRate : options.options?.hourlyRate?.value ?? quote.hourly_rate
  )

  const liveQuote: Partial<QuoteDocument> = {
    ...quote,
    hourly_rate: liveHourlyRate,
    terms: termsValue,
    presentationTemplate: presentationValue
  }

  const updatedQuote: Partial<QuoteDocument> = {
    ...replaceLineItems({
      quote: rates ? { ...replaceRates({ quote: liveQuote, rates, options, calculate: false }) } : liveQuote,
      options
    })
  }

  return updatedQuote
}

function getOptionsValues({ quote, options }: { quote: Partial<QuoteDocument>; options: OptionsDocument }): {
  presentationValue: PresentationOption
  termsValue: QuoteTerms
} {
  // Need to check if the quote type has a specific terms or presentation type
  // else need to use the default from quote, and get the latest version from options
  const intExtOption = options.options?.intExtOptions?.values[quote?.int_ext?.value]
  const presentationKey = quote?.presentationTemplate?.templateCustom
    ? quote?.presentationTemplate?.key ?? 'default'
    : intExtOption?.defaultPresentation ?? quote?.presentationTemplate?.key ?? 'default'
  const termsKey = intExtOption?.defaultTerms ?? quote?.terms?.name

  const presentationValue = quote?.presentationTemplate?.templateCustom
    ? quote?.presentationTemplate
    : options.options?.presentationOptions?.values?.[presentationKey] ?? quote?.presentationTemplate
  const termsValue = options.options?.terms?.values?.[termsKey] ?? quote?.terms

  return { presentationValue, termsValue }
}
